import {
  DevelopmentClient,
  IDevelopmentClient,
} from '@app/models/DevelopmentClient'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IClientsListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IDevelopmentClient>
  readonly totalItems: number
}

const ClientsListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<DevelopmentClient>(),
  totalItems: 0,
})

export class ClientsListState extends ClientsListStateRecord
  implements IClientsListState {
  public static fromJS(initialState?: Partial<IClientsListState>) {
    if (!initialState) {
      return new ClientsListState()
    }

    const { items, ...rest } = initialState
    return new ClientsListState({
      items: items
        ? List(items.map((i) => DevelopmentClient.fromJS(i)))
        : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<DevelopmentClient>
  public readonly totalItems: number

  constructor(props?: Partial<ClientsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ClientsListActionTypes {
  LoadClients = 'CLIENTS_LIST::LOAD_CLIENTS',
  LoadClientsFailure = 'CLIENTS_LIST::LOAD_CLIENTS_FAILURE',
  LoadClientsRequest = 'CLIENTS_LIST::LOAD_CLIENTS_REQUEST',
  LoadClientsSuccess = 'CLIENTS_LIST::LOAD_CLIENTS_SUCCESS',
  SetFilterText = 'CLIENTS_LIST::SET_FILTER_TEXT',
}

interface ILoadClientsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ClientsListActionTypes.LoadClients
}

interface ILoadClientsFailureAction {
  error: string
  type: ClientsListActionTypes.LoadClientsFailure
}

interface ILoadClientsRequestAction {
  type: ClientsListActionTypes.LoadClientsRequest
}

interface ILoadClientsSuccessAction {
  append: boolean
  items: DevelopmentClient[]
  totalItems: number
  type: ClientsListActionTypes.LoadClientsSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: ClientsListActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadClientsFailureAction
  | ILoadClientsRequestAction
  | ILoadClientsSuccessAction
  | ISetFilterTextAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadClients(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadClientsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ClientsListActionTypes.LoadClients,
  }
}

function loadClientsFailure(error: string): ILoadClientsFailureAction {
  return {
    error,
    type: ClientsListActionTypes.LoadClientsFailure,
  }
}

function loadClientsRequest(): ILoadClientsRequestAction {
  return {
    type: ClientsListActionTypes.LoadClientsRequest,
  }
}

function loadClientsSuccess(
  items: DevelopmentClient[],
  append: boolean,
  totalItems: number,
): ILoadClientsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ClientsListActionTypes.LoadClientsSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ClientsListActionTypes.SetFilterText,
  }
}

export const actionCreators = {
  loadClients,
  setFilterText,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ClientsListState> = (
  state: ClientsListState = new ClientsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ClientsListActionTypes.LoadClientsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ClientsListState

    case ClientsListActionTypes.LoadClientsRequest:
      return state.set('isLoading', true) as ClientsListState

    case ClientsListActionTypes.LoadClientsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<DevelopmentClient>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as ClientsListState

    case ClientsListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as ClientsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadClientsSaga(action: ILoadClientsAction) {
  try {
    yield put(loadClientsRequest())

    const currentState: ClientsListState = yield select(
      (state: ApplicationState) => state.clientList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/DevelopmentClients'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: DevelopmentClient[] = result.results.map((c: any) =>
        DevelopmentClient.fromJS(c),
      )
      yield put(
        loadClientsSuccess(contacts, action.append, result.paging.total),
      )
    } else {
      yield put(loadClientsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadClientsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadClients() {
  yield takeLatest(ClientsListActionTypes.LoadClients, loadClientsSaga)
}

export function* clientListRootSaga() {
  yield fork(watchLoadClients)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): ClientsListState =>
  state.clientList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
